import {
  Capacitor,
} from "@capacitor/core";

import {
  DRIVER_APP_VERSION_NAME,
  DRIVER_APP_VERSION_CODE,
} from "../appVersion.js";

// REISOR_ANDROID_1_0_87_VERSION_TRANSPORT_V2
//
// Android identity rules:
// - Android version markers are emitted ONLY from a real Capacitor Android runtime.
// - browser/web Driver HUB traffic must never be classified as Android.
// - versionName is telemetry only.
// - versionCode is used by controlled update policy.
// - HTTP 426 remains globally consumable by App.jsx.

export const DRIVER_APP_PLATFORM_QUERY_KEY =
  "reisorAppPlatform";

export const DRIVER_APP_VERSION_QUERY_KEY =
  "reisorAppVersionCode";

export const DRIVER_APP_VERSION_NAME_QUERY_KEY =
  "reisorAppVersionName";

export const DRIVER_APP_UPDATE_REQUIRED_EVENT =
  "reisor_app_update_required";

const REISOR_API_HOSTS =
  new Set([
    "backend.cargo-platform.online",
    "driver.cargo-platform.online",
    "cargo-platform.online",
    "www.cargo-platform.online",
    "localhost",
    "127.0.0.1",
  ]);

export function
isDriverAndroidNativeRuntime() {
  try {
    return (
      Capacitor?.isNativePlatform?.() === true &&
      String(
        Capacitor?.getPlatform?.() || ""
      )
        .trim()
        .toLowerCase() === "android"
    );
  } catch {
    return false;
  }
}

function currentHref() {
  try {
    return String(
      window?.location?.href ||
      "https://driver.cargo-platform.online/"
    );
  } catch {
    return "https://driver.cargo-platform.online/";
  }
}

function currentHost() {
  try {
    return String(
      window?.location?.hostname || ""
    ).toLowerCase();
  } catch {
    return "";
  }
}

function isAbsoluteUrl(value) {
  return /^[a-z][a-z0-9+.-]*:/i.test(
    String(value || "")
  );
}

function isEligibleApiUrl(url) {
  if(
    !url ||
    !/^\/api(?:\/|$)/.test(
      String(url.pathname || "")
    )
  ){
    return false;
  }

  const host=
    String(
      url.hostname || ""
    ).toLowerCase();

  if(
    REISOR_API_HOSTS.has(host)
  ){
    return true;
  }

  const ownHost=
    currentHost();

  return Boolean(
    ownHost &&
    host === ownHost
  );
}

export function
decorateDriverApiUrlForAndroid(
  rawUrl
) {
  if(
    typeof rawUrl !== "string" ||
    !rawUrl
  ){
    return rawUrl;
  }

  let url;

  try {
    url=
      new URL(
        rawUrl,
        currentHref()
      );
  } catch {
    return rawUrl;
  }

  if(
    !isEligibleApiUrl(url)
  ){
    return rawUrl;
  }

  url.searchParams.set(
    DRIVER_APP_PLATFORM_QUERY_KEY,
    "android"
  );

  url.searchParams.set(
    DRIVER_APP_VERSION_QUERY_KEY,
    String(
      DRIVER_APP_VERSION_CODE
    )
  );

  url.searchParams.set(
    DRIVER_APP_VERSION_NAME_QUERY_KEY,
    String(
      DRIVER_APP_VERSION_NAME
    )
  );

  if(
    isAbsoluteUrl(rawUrl)
  ){
    return url.toString();
  }

  return (
    url.pathname +
    url.search +
    url.hash
  );
}

export function
decorateDriverApiUrl(
  rawUrl
) {
  if(
    !isDriverAndroidNativeRuntime()
  ){
    return rawUrl;
  }

  return decorateDriverApiUrlForAndroid(
    rawUrl
  );
}

function normalizedUpdatePayload(
  data
) {
  const policy=
    data?.policy &&
    typeof data.policy === "object"
      ? {
          ...data.policy,
          updateRequired: true,
        }
      : {
          updateRequired: true,
        };

  return {
    code:
      String(
        data?.code ||
        data?.error ||
        "APP_UPDATE_REQUIRED"
      ),

    policy,
  };
}

export async function
dispatchDriverAppUpdateRequired(
  response
) {
  if(
    !response ||
    Number(response.status) !== 426
  ){
    return false;
  }

  let data={};

  try {
    data=
      await response
        .clone()
        .json();
  } catch {}

  const code=
    String(
      data?.code ||
      data?.error ||
      ""
    );

  if(
    code &&
    code !== "APP_UPDATE_REQUIRED"
  ){
    return false;
  }

  const detail=
    normalizedUpdatePayload(
      data
    );

  try {
    window.dispatchEvent(
      new CustomEvent(
        DRIVER_APP_UPDATE_REQUIRED_EVENT,
        { detail }
      )
    );
  } catch {
    return false;
  }

  return true;
}
